import { useRouteQuery } from '@/core/browser/route-query.composable';
import { QueryKeys } from '@/shared/config/route-query.config';
import { computed, reactive, watch } from 'vue';
import {
    BaseProductItem,
    BasketCheckoutModel,
    BasketCheckoutModelSummary,
    BasketInvoiceAddress,
    DeliveryTimesForCustomerDelivery,
    EarliestCustomerDeliveryDates,
    PickUpPointsForDelivery,
    PickUpTimesForCustomerDelivery,
    SetDeliveryTimeRequest,
    B2BClientService,
    DeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery,
    PaymentInitializeResult,
    PartnerAddress,
    PaymentOptionType,
    DeliveryType,
    CustomerDeliveryCheckOutDeliverable,
    CheckOutDeliverable,
} from '@/b2b/shared/api/server-apis';
import useBasket from '@/b2b/basket/useBasket';
import utilitiesService from '@/shared/utilities.service';
import { LinkModel } from '@/types/B2BcontentServerContract';
import { maybeSoftRedirect, softRedirect } from '@/core/navigation/redirect.service';
import siteContextStore from '@/shared/content/site-context.store';

export interface ICheckoutState {
    step: number;
    basket: BasketCheckoutModel;
    deliveryInfo: DeliverableItemsDeliveryInfo;
    itemIdsOutOfStock: number[];
    receiptPageLink: LinkModel;
}

export interface DeliverableItemsDeliveryInfo {
    [id: number]: EarliestCustomerDeliveryDates,
}

const state: ICheckoutState = reactive({
    step: 0,
    basket: {} as BasketCheckoutModel,
    deliveryInfo: {} as DeliverableItemsDeliveryInfo,
    selectedPaymentOption: null,
    itemIdsOutOfStock: [] as number[],
    showChooseSalesPersonDialog: false,
    receiptPageLink: {} as LinkModel,
});

export function useCheckout() {
    const { basketId, get: refreshBasket } = useBasket();
    const stepQuery = useRouteQuery<number>(QueryKeys.CheckoutStep);

    const step = computed<number>(() => {
        return state.step;
    });

    const deliverableCount = computed<number>(() => {
        return state.basket.deliverables?.length ?? 0;
    });

    const isLastStep = computed<boolean>(() => {
        return state.basket.deliverables?.length === state.step + 1 ?? false;
    });

    const items = computed<BaseProductItem[]>(() => {
        return state.basket.items ?? [] as BaseProductItem[];
    });

    const summary = computed<BasketCheckoutModelSummary>(() => {
        return state.basket.summary ?? {} as BasketCheckoutModelSummary;
    });

    const deliveryInfo = computed<DeliverableItemsDeliveryInfo>(() => {
        return state.deliveryInfo ?? {} as DeliverableItemsDeliveryInfo;
    });

    const invoiceAddress = computed<BasketInvoiceAddress | undefined>(() => {
        return state.basket.invoiceAddress;
    });

    const basket = computed<BasketCheckoutModel>(() => {
        return state.basket ?? {} as BasketCheckoutModel;
    });

    const itemIdsOutOfStock = computed<number[]>(() => {
        return state.itemIdsOutOfStock;
    });

    const receiptPageLink = computed<LinkModel>(() => {
        return state.receiptPageLink;
    });

    const hasSufficientPayments = computed<boolean>(() => {
        return state.basket.hasSufficientPayments;
    });

    const init = async(receiptPageLink: LinkModel): Promise<void> => {
        state.receiptPageLink = receiptPageLink;
        const response = await B2BClientService.getBasketForCheckOut(basketId.value).then(utilitiesService.unwrapModel);
        if (response?.items.length === 0) {
            softRedirect(siteContextStore.settings.basketPageUrl);
        }
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const getAllPreviousDeliverables = (index: number): CheckOutDeliverable[] => {
        const previousDeliverables = state.basket.deliverables.slice(0, index);
        return previousDeliverables;
    };

    const getHasPreviousShippingAddress = (deliverableId: number): boolean => {
        const deliverableIndex = basket.value.deliverables.findIndex(deliverable => deliverable.id === deliverableId);
        if (deliverableIndex < 1) {
            return false;
        }
        return getAllPreviousDeliverables(deliverableIndex).some(deliverable => {
            const maybeCustomerDeliveryCheckOutDeliverable = deliverable as CustomerDeliveryCheckOutDeliverable;
            return maybeCustomerDeliveryCheckOutDeliverable.deliveryType !== undefined ? maybeCustomerDeliveryCheckOutDeliverable.deliveryType === DeliveryType.Shipping : false;
        });
    };

    const setDeliveryAddress = async(addressId: string | null): Promise<void> => {
        const response = await B2BClientService
            .setDeliveryAddress(basketId.value, addressId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const getPickUpErrorMessageId = (id: number) => `pickUpErrorMessageId-${id}`;

    const setDeliveryTime = async(deliverableId: number, request: SetDeliveryTimeRequest, messageId?: string): Promise<void> => {
        const response = await B2BClientService
            .setDeliveryTime(basketId.value, deliverableId, request, messageId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const setDeliveryInformationForItems = async(deliverableId: number): Promise<void> => {
        const response = await B2BClientService
            .getEarliestDeliveryDateForCustomerDelivery(basketId.value, deliverableId)
            .then(utilitiesService.unwrapModel);
        if (response) {
            state.deliveryInfo[deliverableId] = response;
        }
    };

    const clearDeliveryInformationForItems = (deliverableId: number): void => {
        if (state.deliveryInfo[deliverableId]) {
            delete state.deliveryInfo[deliverableId];
        }
    };

    const getDeliveryTimesForCustomerDelivery = async(deliverableId: number): Promise<DeliveryTimesForCustomerDelivery> => {
        const response = await B2BClientService
            .getDeliveryTimesForCustomerDelivery(basketId.value, deliverableId)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as DeliveryTimesForCustomerDelivery;
    };

    const getPickUpPointsForDelivery = async(deliverableId: number): Promise<PickUpPointsForDelivery> => {
        const response = await B2BClientService
            .getPickUpPointsForDelivery(basketId.value, deliverableId)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as PickUpPointsForDelivery;
    };

    const getPickUpTimesForCustomerDelivery = async(deliverableId: number, hubId: string): Promise<PickUpTimesForCustomerDelivery> => {
        const response = await B2BClientService
            .getPickUpTimesForCustomerDelivery(basketId.value, deliverableId, hubId)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as PickUpTimesForCustomerDelivery;
    };

    const splitDelivery = async(deliveryId: number, itemIds: number[]): Promise<void> => {
        const response = await B2BClientService
            .splitDelivery(basketId.value, deliveryId, itemIds)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const removeSplitDelivery = async(deliveryId: number): Promise<void> => {
        const response = await B2BClientService
            .removeSplitDelivery(basketId.value, deliveryId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const removeSplitDeliveries = async(deliveryId: number): Promise<BasketCheckoutModel> => {
        const response = await B2BClientService
            .removeSplitDeliveries(basketId.value, deliveryId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
        return state.basket;
    };

    const getDeliveryDateAndFreightPriceForSplitItems = async(deliveryId: number, itemIds: number[]): Promise<DeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery> => {
        const response = await B2BClientService
            .getDeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery(basketId.value, deliveryId, itemIds)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as DeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery;
    };

    const getDeliveryAddresses = async(): Promise<PartnerAddress[]> => {
        const response = await B2BClientService
            .getDeliveryAddresses()
            .then(utilitiesService.unwrapModel);
        return response ?? [] as PartnerAddress[];
    };

    const initializePayment = async(): Promise<PaymentInitializeResult> => {
        const response = await B2BClientService
            .initializePayment(basketId.value)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as PaymentInitializeResult;
    };

    const submitOrderWithB2BPayment = async(): Promise<void> => {
        const response = await B2BClientService
            .submitOrderWithB2BPayment(basketId.value)
            .then(utilitiesService.unwrapModel);
        if (response?.orderNumber) {
            await redirectToReceipt(response.orderNumber);
        }
    };

    const submitOrderWithSufficientPaymentsInBasket = async(): Promise<void> => {
        await submitOrderWithB2BPayment();
    };

    const isAllowedBankTransfer = computed(() => {
        const paymentOptions = state.basket?.paymentOptions ?? [];
        const bankTransferPaymentOption = paymentOptions.find(option => option.type === PaymentOptionType.BankTransfer);
        return bankTransferPaymentOption !== undefined;
    });

    const setItemIdsOutOfStock = (items?: number[]): void => {
        state.itemIdsOutOfStock = items?.length ? items : [];
    };

    const clearItemIdsOutOfStock = (): void => {
        state.itemIdsOutOfStock.length = 0;
    };

    const updateStep = (step: number): void => {
        stepQuery.value = step;
    };

    const gotoNextStep = (): void => {
        if (!isLastStep.value) {
            stepQuery.value = state.step + 1;
        }
    };

    const redirectToReceipt = async(orderNumber: string) => {
        const redirectLinkWithFallback = receiptPageLink.value ?? { url: siteContextStore.settings.frontPageUrl, spaLink: false };
        const { url: path, spaLink } = redirectLinkWithFallback;
        const url = `${path}?${QueryKeys.Id}=${orderNumber}`;
        maybeSoftRedirect({
            url,
            spaLink,
        });
        await refreshBasket();
    };

    watch(stepQuery, step => {
        state.step = step;
    }, { immediate: true });

    return {
        items,
        summary,
        invoiceAddress,
        deliveryInfo,
        deliverableCount,
        basket,
        hasSufficientPayments,
        step,
        isLastStep,
        itemIdsOutOfStock,
        receiptPageLink,
        isAllowedBankTransfer,
        init,
        getHasPreviousShippingAddress,
        setDeliveryAddress,
        setDeliveryInformationForItems,
        clearDeliveryInformationForItems,
        getPickUpErrorMessageId,
        setDeliveryTime,
        getDeliveryTimesForCustomerDelivery,
        getPickUpPointsForDelivery,
        getPickUpTimesForCustomerDelivery,
        setItemIdsOutOfStock,
        clearItemIdsOutOfStock,
        updateStep,
        gotoNextStep,
        splitDelivery,
        removeSplitDelivery,
        removeSplitDeliveries,
        getDeliveryDateAndFreightPriceForSplitItems,
        getDeliveryAddresses,
        initializePayment,
        submitOrderWithB2BPayment,
        submitOrderWithSufficientPaymentsInBasket,
        redirectToReceipt,
    };
}
